import { useState, useEffect, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import PublicNav from "@/components/PublicNav";
import { Building2, RefreshCw, MapPin, X, ExternalLink, ShieldCheck, Settings, FileText } from "lucide-react";

const LCS_ROLES = ["super_admin", "admin_lcs", "recruteur_lcs", "assistant_recruteur"];
const ADMIN_ROLES = ["super_admin", "admin_lcs"];
const BAND_STYLE = {
  A: "bg-green-100 text-green-800",
  B: "bg-amber-100 text-amber-800",
  C: "bg-slate-100 text-slate-600",
};
const STATUS_LABELS = {
  historique_eimt: "Historique EIMT",
  offre_active: "Offre actuelle",
  besoin_confirme: "Besoin confirmé",
};

export default function Prospection() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [sources, setSources] = useState([]);
  const [data, setData] = useState({ items: [], total: 0 });
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [syncing, setSyncing] = useState(false);
  const [page, setPage] = useState(1);
  const [tab, setTab] = useState("employeurs");
  const [filters, setFilters] = useState({ q: "", province: "", band: "", eimt: "" });

  const allowed = user && LCS_ROLES.includes(user.role);
  const isAdmin = user && ADMIN_ROLES.includes(user.role);

  useEffect(() => {
    if (!authLoading && !user) navigate("/login", { replace: true });
    else if (!authLoading && user && !LCS_ROLES.includes(user.role)) navigate("/dashboard", { replace: true });
  }, [user, authLoading, navigate]);

  const loadMeta = useCallback(async () => {
    const [s, src] = await Promise.all([api.get("/prospection/stats"), api.get("/prospection/sources")]);
    setStats(s.data);
    setSources(src.data);
  }, []);

  const loadEmployers = useCallback(async () => {
    const params = new URLSearchParams({ page: String(page), limit: "25" });
    Object.entries(filters).forEach(([k, v]) => v && params.set(k, v));
    const res = await api.get(`/prospection/employers?${params}`);
    setData(res.data);
  }, [page, filters]);

  useEffect(() => {
    if (!allowed) return;
    (async () => {
      setError("");
      setLoading(true);
      try {
        await Promise.all([loadMeta(), loadEmployers()]);
      } catch (err) {
        setError(formatApiError(err));
      } finally {
        setLoading(false);
      }
    })();
  }, [allowed, loadMeta, loadEmployers]);

  const applyFilters = (patch) => {
    setPage(1);
    setFilters((f) => ({ ...f, ...patch }));
  };

  const triggerSync = async () => {
    setSyncing(true);
    setError("");
    try {
      await api.post("/prospection/sync", {});
      const known = new Set();
      for (let i = 0; i < 60; i++) {
        await new Promise((r) => setTimeout(r, 5000));
        const runs = await api.get("/prospection/runs");
        const active = runs.data.find((r) => r.status === "running" && !known.has(r.run_id));
        const freshDone = runs.data.find((r) => r.trigger === "manual" && r.status !== "running" && !known.has(r.run_id));
        if (freshDone && !active) break;
      }
      await Promise.all([loadMeta(), loadEmployers()]);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSyncing(false);
    }
  };

  const openDetail = async (employerId) => {
    try {
      const res = await api.get(`/prospection/employers/${employerId}`);
      setDetail(res.data);
    } catch (err) {
      setError(formatApiError(err));
    }
  };

  if (!user || !allowed) return null;
  const lastRun = sources[0]?.last_run;
  const pages = Math.max(1, Math.ceil(data.total / 25));

  return (
    <div data-testid="prospection-page" className="min-h-screen bg-slate-50">
      <PublicNav />

      <div className="bg-brand-navy">
        <div className="max-w-6xl mx-auto px-6 py-12 flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-extrabold text-white flex items-center gap-3">
              <Building2 className="w-7 h-7 text-brand-turquoise" />
              Prospection employeurs
            </h1>
            <p className="text-white/70 text-sm mt-1">
              Employeurs canadiens à potentiel TET — données ouvertes EIMT, score de priorité explicable.
            </p>
          </div>
          {isAdmin && (
            <div className="flex items-center gap-2">
              <Link
                data-testid="config-link"
                to="/prospection/configuration"
                className="flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white font-semibold text-sm rounded-lg px-4 py-2.5 transition-colors"
              >
                <Settings className="w-4 h-4" />
                Configuration
              </Link>
              <Link
                data-testid="brochures-link"
                to="/prospection/brochures"
                className="flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white font-semibold text-sm rounded-lg px-4 py-2.5 transition-colors"
              >
                <FileText className="w-4 h-4" />
                Brochures
              </Link>
              <button
                data-testid="sync-button"
                onClick={triggerSync}
                disabled={syncing}
                className="flex items-center gap-2 bg-brand-turquoise hover:bg-brand-turquoise/90 disabled:opacity-50 text-white font-semibold text-sm rounded-lg px-4 py-2.5 transition-colors"
              >
                <RefreshCw className={`w-4 h-4 ${syncing ? "animate-spin" : ""}`} />
                {syncing ? "Synchronisation…" : "Synchroniser EIMT"}
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-8 space-y-6">
        <div data-testid="prospection-tabs" className="flex gap-1.5 bg-white border border-slate-200 rounded-lg p-1 w-fit">
          {[["employeurs", "Employeurs"], ["campagnes", "Campagnes"], ["canal", "Canal à valider"]].map(([k, label]) => (
            <button key={k} data-testid={`tab-${k}`} onClick={() => setTab(k)}
              className={`text-xs font-semibold rounded-md px-4 py-2 transition-colors ${
                tab === k ? "bg-brand-navy text-white" : "text-slate-500 hover:text-brand-navy"
              }`}>{label}</button>
          ))}
        </div>
        {tab === "employeurs" && (<>
        {error && (
          <div data-testid="prospection-error" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-4 py-3">{error}</div>
        )}

        {sources[0] && (
          <div data-testid="source-status" className="bg-white rounded-xl border border-slate-200 px-5 py-3 flex flex-wrap items-center gap-x-6 gap-y-1 text-xs text-slate-600">
            <span className="font-semibold text-brand-navy">{sources[0].label}</span>
            <span>Statut : <span className={sources[0].status === "ok" ? "text-green-700 font-medium" : "text-amber-700 font-medium"}>{sources[0].status === "ok" ? "Opérationnelle" : sources[0].status}</span></span>
            <span>Fichiers traités : {sources[0].resources_processed}</span>
            {lastRun && <span>Dernier import : {lastRun.status} ({lastRun.records_new} nouvelles lignes)</span>}
            {stats && stats.jobs_pending > 0 && <span className="text-brand-blue font-medium">{stats.jobs_pending} tâche(s) en file</span>}
            {stats && stats.jobs_failed > 0 && <span className="text-red-600 font-medium">{stats.jobs_failed} tâche(s) en quarantaine</span>}
          </div>
        )}

        {stats && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-white rounded-xl border border-slate-200 p-5">
              <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Entreprises découvertes</p>
              <p data-testid="stat-total" className="text-3xl font-bold text-brand-navy mt-1">{stats.employers_total}</p>
            </div>
            <div className="bg-white rounded-xl border border-slate-200 p-5">
              <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Historique EIMT documenté</p>
              <p data-testid="stat-eimt" className="text-3xl font-bold text-brand-blue mt-1">{stats.employers_with_eimt_history}</p>
            </div>
            <div className="bg-white rounded-xl border border-slate-200 p-5">
              <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Offre actuelle vérifiée</p>
              <p data-testid="stat-offers" className="text-3xl font-bold text-brand-navy mt-1">{stats.employers_with_current_offer}</p>
            </div>
            <div className="bg-white rounded-xl border border-slate-200 p-5">
              <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Priorité</p>
              <div className="flex items-center gap-2 mt-2">
                {["A", "B", "C"].map((b) => (
                  <span key={b} data-testid={`stat-band-${b.toLowerCase()}`} className={`text-xs font-bold rounded-full px-2.5 py-1 ${BAND_STYLE[b]}`}>
                    {b} · {stats.by_band[b] || 0}
                  </span>
                ))}
              </div>
            </div>
          </div>
        )}

        <div data-testid="prospection-filters" className="bg-white rounded-xl border border-slate-200 p-4 flex flex-wrap gap-3">
          <input
            data-testid="filter-q"
            value={filters.q}
            onChange={(e) => applyFilters({ q: e.target.value })}
            placeholder="Rechercher une entreprise…"
            className="flex-1 min-w-[220px] text-sm border border-slate-200 rounded-lg px-3.5 py-2.5 focus:outline-none focus:ring-2 focus:ring-brand-turquoise"
          />
          <select data-testid="filter-province" value={filters.province} onChange={(e) => applyFilters({ province: e.target.value })}
            className="text-sm border border-slate-200 rounded-lg px-3 py-2.5 bg-white">
            <option value="">Toutes les provinces</option>
            {(stats?.by_province || []).map((p) => (
              <option key={p.province} value={p.province}>{p.province} ({p.count})</option>
            ))}
          </select>
          <select data-testid="filter-band" value={filters.band} onChange={(e) => applyFilters({ band: e.target.value })}
            className="text-sm border border-slate-200 rounded-lg px-3 py-2.5 bg-white">
            <option value="">Toutes priorités</option>
            <option value="A">Priorité A (75-100)</option>
            <option value="B">Priorité B (50-74)</option>
            <option value="C">Priorité C (&lt; 50)</option>
          </select>
          <label className="flex items-center gap-2 text-sm text-slate-600 px-2"
            title="Toutes les entreprises du corpus actuel ont un historique EIMT ; ce filtre prendra son sens avec les prochaines sources (offres, registres).">
            <input data-testid="filter-eimt" type="checkbox" checked={filters.eimt === "oui"}
              onChange={(e) => applyFilters({ eimt: e.target.checked ? "oui" : "" })}
              className="w-4 h-4 accent-brand-turquoise" />
            Historique EIMT
          </label>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <table data-testid="employers-table" className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
                <th className="px-5 py-3">Entreprise</th>
                <th className="px-5 py-3">Localisation</th>
                <th className="px-5 py-3">Statut</th>
                <th className="px-5 py-3 text-center">Trimestres EIMT</th>
                <th className="px-5 py-3 text-right">Score</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={5} data-testid="employers-loading" className="px-5 py-10 text-center">
                  <span className="inline-block w-6 h-6 border-2 border-slate-200 border-t-brand-turquoise rounded-full animate-spin" />
                </td></tr>
              )}
              {!loading && data.items.length === 0 && (
                <tr><td colSpan={5} data-testid="employers-empty" className="px-5 py-10 text-center text-slate-400">
                  Aucune entreprise ne correspond aux filtres.
                </td></tr>
              )}
              {data.items.map((e) => (
                <tr key={e.employer_id} data-testid={`employer-row-${e.employer_id}`} onClick={() => openDetail(e.employer_id)}
                  className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer transition-colors">
                  <td className="px-5 py-3.5 font-semibold text-brand-navy">{e.legal_name}</td>
                  <td className="px-5 py-3.5 text-slate-600">
                    <span className="flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5 text-slate-400" />{e.city || "—"}, {e.province}</span>
                  </td>
                  <td className="px-5 py-3.5">
                    <span className="text-xs font-medium bg-blue-50 text-brand-blue rounded-full px-2.5 py-1">
                      {STATUS_LABELS[e.recruitment_status] || e.recruitment_status}
                    </span>
                  </td>
                  <td className="px-5 py-3.5 text-center text-slate-600">{e.eimt_periods.length}</td>
                  <td className="px-5 py-3.5 text-right">
                    <span className={`inline-flex items-center gap-1.5 text-xs font-bold rounded-full px-2.5 py-1 ${BAND_STYLE[e.band]}`}>
                      {e.band} · {e.score}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="flex items-center justify-between px-5 py-3 border-t border-slate-100 text-sm text-slate-600">
            <span data-testid="employers-count">{data.total} entreprise(s)</span>
            <div className="flex gap-2">
              <button data-testid="page-prev" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1}
                className="px-3 py-1.5 rounded-lg border border-slate-200 disabled:opacity-40 hover:bg-slate-50">Précédent</button>
              <span className="px-2 py-1.5">{page} / {pages}</span>
              <button data-testid="page-next" onClick={() => setPage((p) => Math.min(pages, p + 1))} disabled={page >= pages}
                className="px-3 py-1.5 rounded-lg border border-slate-200 disabled:opacity-40 hover:bg-slate-50">Suivant</button>
            </div>
          </div>
        </div>
      </>)}
        {tab === "campagnes" && <CampagnesTab isAdmin={isAdmin} />}
        {tab === "canal" && <CanalTab isAdmin={isAdmin} />}
      </div>

      {detail && (
        <div data-testid="employer-detail" className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 overflow-y-auto py-10 px-4"
          onClick={() => setDetail(null)}>
          <div className="bg-white rounded-2xl max-w-3xl w-full p-6 sm:p-8 space-y-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between gap-4">
              <div>
                <h2 className="text-xl font-extrabold text-brand-navy">{detail.employer.legal_name}</h2>
                <p className="text-sm text-slate-500 mt-1">{detail.employer.address || `${detail.employer.city}, ${detail.employer.province}`}</p>
              </div>
              <button data-testid="detail-close" onClick={() => setDetail(null)}
                className="p-2 rounded-lg hover:bg-slate-100 text-slate-500"><X className="w-5 h-5" /></button>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
              <div className="bg-slate-50 rounded-lg p-3"><p className="text-xs text-slate-500">Courriel</p><p className="font-medium text-brand-navy">{detail.employer.email}</p></div>
              <div className="bg-slate-50 rounded-lg p-3"><p className="text-xs text-slate-500">Téléphone</p><p className="font-medium text-brand-navy">{detail.employer.phone}</p></div>
              <div className="bg-slate-50 rounded-lg p-3"><p className="text-xs text-slate-500">Site web</p><p className="font-medium text-brand-navy">{detail.employer.website}</p></div>
              <div className="bg-slate-50 rounded-lg p-3"><p className="text-xs text-slate-500">Recrutement actuel</p><p className="font-medium text-brand-navy">{detail.employer.has_current_offer ? "Oui (vérifié)" : "Non confirmé"}</p></div>
              <div className="bg-slate-50 rounded-lg p-3"><p className="text-xs text-slate-500">Besoin confirmé</p><p className="font-medium text-brand-navy">{detail.employer.need_confirmed ? "Oui" : "Non"}</p></div>
              <div className="bg-slate-50 rounded-lg p-3"><p className="text-xs text-slate-500">Admissibilité</p><p className="font-medium text-brand-navy">À analyser</p></div>
            </div>

            {detail.score_detail && (
              <div data-testid="score-criteria">
                <h3 className="font-semibold text-brand-navy mb-3 flex items-center gap-2">
                  <ShieldCheck className="w-4 h-4 text-brand-turquoise" />
                  Score {detail.score_detail.score}/100 — Priorité {detail.score_detail.band}
                </h3>
                <div className="space-y-2">
                  {detail.score_detail.criteria.map((c) => (
                    <div key={c.key} className="flex items-start justify-between gap-3 text-sm border border-slate-100 rounded-lg px-3.5 py-2.5">
                      <div>
                        <p className={`font-medium ${c.satisfied ? "text-brand-navy" : "text-slate-400"}`}>{c.label}</p>
                        <p className="text-xs text-slate-400">{c.note}</p>
                        {c.evidence.length > 0 && <p className="text-xs text-brand-turquoise mt-0.5">Preuves : {c.evidence.slice(0, 6).join(", ")}</p>}
                      </div>
                      <span className={`text-xs font-bold whitespace-nowrap ${c.satisfied ? "text-green-700" : "text-slate-400"}`}>
                        {c.points}/{c.max}
                      </span>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-slate-400 mt-2">{detail.score_detail.disclaimer}</p>
              </div>
            )}

            <div>
              <h3 className="font-semibold text-brand-navy mb-3">Signaux EIMT documentés ({detail.signals.length})</h3>
              <div data-testid="signals-list" className="max-h-64 overflow-y-auto space-y-2">
                {detail.signals.map((s) => (
                  <div key={s.signal_id} className="text-sm border border-slate-100 rounded-lg px-3.5 py-2.5 flex items-start justify-between gap-3">
                    <div>
                      <p className="font-medium text-brand-navy">{s.occupation || "Profession non précisée"}</p>
                      <p className="text-xs text-slate-500">{s.period} · {s.stream || "Volet n/d"} · {s.positions_approved} poste(s)</p>
                    </div>
                    <a href={s.source_url} target="_blank" rel="noopener noreferrer" data-testid={`signal-source-${s.signal_id}`}
                      className="text-brand-blue hover:text-brand-turquoise shrink-0"><ExternalLink className="w-4 h-4" /></a>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const CAMPAIGN_STATUS = { brouillon: "Brouillon", active: "Active", en_pause: "En pause" };
const EVENT_LABELS = {
  envoye: "Envoyé", echec_envoi: "Échec d'envoi", canal_a_valider: "Canal à valider",
  bloque_desabonnement: "Bloqué (désabonné)", bloque_refus: "Bloqué (refus)", tache_plateforme: "Tâche plateforme",
};

function CampagnesTab({ isAdmin }) {
  const [campaigns, setCampaigns] = useState([]);
  const [form, setForm] = useState({ name: "", subject: "", body: "" });
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const load = useCallback(async () => {
    const res = await api.get("/prospection/campaigns");
    setCampaigns(res.data);
  }, []);

  useEffect(() => { load().catch((e) => setError(formatApiError(e))); }, [load]);

  const create = async () => {
    setError(""); setNotice("");
    try {
      await api.post("/prospection/campaigns", { ...form, segment: {} });
      setForm({ name: "", subject: "", body: "" });
      setNotice("Campagne créée (brouillon).");
      await load();
    } catch (err) { setError(formatApiError(err)); }
  };

  const act = async (id, action) => {
    setError(""); setNotice("");
    try {
      await api.post(`/prospection/campaigns/${id}/${action}`);
      setNotice(action === "launch" ? "Exécution mise en file — la conformité LCAP est vérifiée pour chaque cible." : "Campagne mise en pause.");
      setTimeout(load, 4000);
    } catch (err) { setError(formatApiError(err)); }
  };

  return (
    <div data-testid="campagnes-tab" className="space-y-6">
      {error && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-4 py-3">{error}</div>}
      {notice && <div data-testid="campaign-notice" className="text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg px-4 py-3">{notice}</div>}

      {isAdmin && (
        <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-3">
          <h2 className="font-semibold text-brand-navy">Nouvelle campagne</h2>
          <p className="text-xs text-slate-500">Variables : {"{entreprise}"}, {"{ville}"}, {"{province}"}. Chaque envoi exige une base légale LCAP vérifiée côté serveur.</p>
          <input data-testid="campaign-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="Nom de la campagne" className="w-full text-sm border border-slate-200 rounded-lg px-3.5 py-2.5" />
          <input data-testid="campaign-subject" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })}
            placeholder="Objet du courriel" className="w-full text-sm border border-slate-200 rounded-lg px-3.5 py-2.5" />
          <textarea data-testid="campaign-body" value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })}
            placeholder="Message (HTML simple)…" rows={4} className="w-full text-sm border border-slate-200 rounded-lg px-3.5 py-2.5" />
          <button data-testid="campaign-create-submit" onClick={create}
            className="bg-brand-navy hover:bg-brand-navy/90 text-white text-sm font-semibold rounded-lg px-4 py-2.5 transition-colors">
            Créer le brouillon
          </button>
        </div>
      )}

      <div className="space-y-3">
        {campaigns.length === 0 && <p data-testid="campaigns-empty" className="text-sm text-slate-400 text-center py-8">Aucune campagne pour le moment.</p>}
        {campaigns.map((c) => (
          <div key={c.campaign_id} data-testid={`campaign-row-${c.campaign_id}`}
            className="bg-white rounded-xl border border-slate-200 p-5 flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="font-semibold text-brand-navy">{c.name}</p>
              <p className="text-xs text-slate-500 mt-0.5">{c.subject}</p>
              <div className="flex flex-wrap gap-2 mt-2 text-xs">
                {Object.entries(c.stats || {}).filter(([, v]) => v > 0).map(([k, v]) => (
                  <span key={k} className="bg-slate-100 text-slate-600 rounded-full px-2.5 py-0.5">{EVENT_LABELS[k] || k} : {v}</span>
                ))}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs font-medium bg-blue-50 text-brand-blue rounded-full px-2.5 py-1">{CAMPAIGN_STATUS[c.status] || c.status}</span>
              {isAdmin && (
                <>
                  <button data-testid={`campaign-launch-${c.campaign_id}`} onClick={() => act(c.campaign_id, "launch")}
                    className="text-xs font-semibold bg-brand-turquoise text-white rounded-lg px-3 py-1.5 hover:bg-brand-turquoise/90">Exécuter</button>
                  <button data-testid={`campaign-pause-${c.campaign_id}`} onClick={() => act(c.campaign_id, "pause")}
                    className="text-xs font-semibold border border-slate-200 rounded-lg px-3 py-1.5 hover:bg-slate-50">Pause</button>
                </>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function CanalTab({ isAdmin }) {
  const [items, setItems] = useState([]);
  const [error, setError] = useState("");
  const [inputs, setInputs] = useState({});

  const load = useCallback(async () => {
    const res = await api.get("/prospection/compliance/pending");
    setItems(res.data);
  }, []);

  useEffect(() => { load().catch((e) => setError(formatApiError(e))); }, [load]);

  const validate = async (employerId, action) => {
    setError("");
    try {
      const entry = inputs[employerId] || {};
      await api.post(`/prospection/compliance/${employerId}/validate-channel`, {
        action,
        channel: "email",
        value: entry.value || "",
        justification: entry.justification || "",
      });
      await load();
    } catch (err) { setError(formatApiError(err)); }
  };

  return (
    <div data-testid="canal-tab" className="space-y-4">
      {error && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-4 py-3">{error}</div>}
      <p className="text-sm text-slate-600 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3">
        Ces entreprises n'ont aucune base légale de communication établie (LCAP). Aucun message automatique ne leur est envoyé.
        Un administrateur peut documenter une permission (ex. consentement obtenu) ou refuser le canal.
      </p>
      {items.length === 0 && <p data-testid="canal-empty" className="text-sm text-slate-400 text-center py-8">Aucun contact en attente de validation.</p>}
      {items.map((p) => (
        <div key={p.employer_id} data-testid={`pending-row-${p.employer_id}`}
          className="bg-white rounded-xl border border-slate-200 p-5 space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <p className="font-semibold text-brand-navy">{p.legal_name}</p>
              <p className="text-xs text-slate-500">{p.city || "—"}, {p.province} · en attente depuis le {new Date(p.since).toLocaleDateString("fr-CA")}</p>
            </div>
          </div>
          {isAdmin && (
            <div className="flex flex-wrap gap-2">
              <input data-testid={`pending-value-${p.employer_id}`} placeholder="Courriel autorisé (optionnel)"
                value={(inputs[p.employer_id] || {}).value || ""}
                onChange={(e) => setInputs({ ...inputs, [p.employer_id]: { ...(inputs[p.employer_id] || {}), value: e.target.value } })}
                className="flex-1 min-w-[200px] text-sm border border-slate-200 rounded-lg px-3 py-2" />
              <input data-testid={`pending-justif-${p.employer_id}`} placeholder="Justification de la permission (obligatoire)"
                value={(inputs[p.employerId] || {}).justification || (inputs[p.employer_id] || {}).justification || ""}
                onChange={(e) => setInputs({ ...inputs, [p.employer_id]: { ...(inputs[p.employer_id] || {}), justification: e.target.value } })}
                className="flex-1 min-w-[240px] text-sm border border-slate-200 rounded-lg px-3 py-2" />
              <button data-testid={`pending-approve-${p.employer_id}`} onClick={() => validate(p.employer_id, "autorise")}
                className="text-xs font-semibold bg-brand-turquoise text-white rounded-lg px-3 py-2 hover:bg-brand-turquoise/90">Autoriser</button>
              <button data-testid={`pending-refuse-${p.employer_id}`} onClick={() => validate(p.employer_id, "refuse")}
                className="text-xs font-semibold bg-red-50 text-red-700 border border-red-200 rounded-lg px-3 py-2 hover:bg-red-100">Refuser</button>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
